import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useCallers, useBlockAssignments, useConstituencies, useCampaigns, useRecordsBlocks } from '../hooks/queries';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Phone, UserPlus, UserCheck, UserX, Trash2, Users, CheckCircle, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CallCentreManagement() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('callers');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedCaller, setSelectedCaller] = useState(null);
  const [selectedConstituency, setSelectedConstituency] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedBlocks, setSelectedBlocks] = useState([]);

  const { data: callers = [], isLoading: loadingCallers } = useCallers(statusFilter);
  const { data: constituencies = [] } = useConstituencies();
  const { data: campaigns = [] } = useCampaigns(selectedConstituency);
  const { data: blocksData } = useRecordsBlocks(selectedConstituency, selectedCampaign);
  const blocks = blocksData?.blocks ?? [];

  const { data: assignmentsData = [], isLoading: loadingAssignments } = useBlockAssignments(undefined, {
    enabled: activeTab === 'assignments',
  });

  const invalidateCallers = () => {
    queryClient.invalidateQueries({ queryKey: ['callers'] });
    queryClient.invalidateQueries({ queryKey: ['blockAssignments'] });
  };

  const updateCallerStatus = async (callerId, newStatus) => {
    try {
      await api.put(`${API}/callers/${callerId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`Caller ${newStatus}`);
      invalidateCallers();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteCaller = async (callerId) => {
    if (!window.confirm('Delete this caller? This will also remove all their assignments.')) return;
    
    try {
      await api.delete(`${API}/callers/${callerId}`, { withCredentials: true });
      toast.success('Caller deleted');
      invalidateCallers();
    } catch (error) {
      toast.error('Failed to delete caller');
    }
  };

  const openAssignDialog = (caller) => {
    setSelectedCaller(caller);
    setSelectedConstituency('');
    setSelectedCampaign('');
    setSelectedBlocks([]);
    setShowAssignDialog(true);
  };

  const assignBlocks = async () => {
    if (!selectedCaller || !selectedConstituency || !selectedCampaign || selectedBlocks.length === 0) {
      toast.error('Please select constituency, campaign, and at least one block');
      return;
    }

    try {
      await api.post(`${API}/block-assignments`, {
        caller_id: selectedCaller.caller_id,
        constituency_id: selectedConstituency,
        campaign_id: selectedCampaign,
        blocks: selectedBlocks
      }, { withCredentials: true });

      toast.success('Blocks assigned successfully');
      setShowAssignDialog(false);
      invalidateCallers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign blocks');
    }
  };

  const deleteAssignment = async (assignmentId) => {
    if (!window.confirm('Remove this assignment?')) return;
    
    try {
      await api.delete(`${API}/block-assignments/${assignmentId}`, { withCredentials: true });
      toast.success('Assignment removed');
      invalidateCallers();
    } catch (error) {
      toast.error('Failed to remove assignment');
    }
  };

  const toggleBlock = (block) => {
    if (selectedBlocks.includes(block)) {
      setSelectedBlocks(selectedBlocks.filter(b => b !== block));
    } else {
      setSelectedBlocks([...selectedBlocks, block]);
    }
  };

  const selectAllBlocks = () => {
    setSelectedBlocks(blocks);
  };

  const pendingCount = callers.filter(c => c.status === 'pending').length;

  return (
    <Layout>
      <div className="p-4 md:p-8">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Phone className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Call Centre Management
            </h1>
          </div>
          <p className="text-muted-foreground">Manage callers and block assignments</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b">
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'callers' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'}`}
            onClick={() => setActiveTab('callers')}
          >
            Callers {pendingCount > 0 && <span className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{pendingCount}</span>}
          </button>
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'assignments' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'}`}
            onClick={() => setActiveTab('assignments')}
          >
            Block Assignments
          </button>
        </div>

        {activeTab === 'callers' && (
          <>
            {/* Filter */}
            <div className="mb-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Callers</SelectItem>
                  <SelectItem value="pending">Pending Approval</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Callers List */}
            {loadingCallers ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              </div>
            ) : callers.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">No callers found</h3>
                  <p className="text-muted-foreground">Callers will appear here once they register</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {callers.map(caller => (
                  <Card key={caller.caller_id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            caller.status === 'approved' ? 'bg-green-100 text-green-600' :
                            caller.status === 'pending' ? 'bg-amber-100 text-amber-600' :
                            'bg-red-100 text-red-600'
                          }`}>
                            {caller.status === 'approved' ? <UserCheck className="h-5 w-5" /> :
                             caller.status === 'pending' ? <Clock className="h-5 w-5" /> :
                             <UserX className="h-5 w-5" />}
                          </div>
                          <div>
                            <h3 className="font-semibold">{caller.name}</h3>
                            <p className="text-sm text-muted-foreground">{caller.email}</p>
                            {caller.phone && <p className="text-sm text-muted-foreground">{caller.phone}</p>}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            caller.status === 'approved' ? 'bg-green-100 text-green-800' :
                            caller.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {caller.status}
                          </span>
                          
                          {caller.status === 'pending' && (
                            <Button size="sm" variant="outline" onClick={() => updateCallerStatus(caller.caller_id, 'approved')}>
                              <UserCheck className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                          )}
                          
                          {caller.status === 'approved' && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => openAssignDialog(caller)}>
                                Assign Blocks
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => updateCallerStatus(caller.caller_id, 'suspended')}>
                                Suspend
                              </Button>
                            </>
                          )}
                          
                          {caller.status === 'suspended' && (
                            <Button size="sm" variant="outline" onClick={() => updateCallerStatus(caller.caller_id, 'approved')}>
                              Reactivate
                            </Button>
                          )}
                          
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteCaller(caller.caller_id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'assignments' && (
          <>
            {assignments.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">No assignments yet</h3>
                  <p className="text-muted-foreground">Assign blocks to approved callers from the Callers tab</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {assignments.map(assignment => (
                  <Card key={assignment.assignment_id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold">{assignment.caller_name}</h3>
                          <p className="text-sm text-muted-foreground">{assignment.caller_email}</p>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {assignment.blocks.map(block => (
                              <span
                                key={block}
                                className={`px-2 py-0.5 text-xs rounded ${
                                  assignment.completed_blocks?.includes(block)
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}
                              >
                                {block} {assignment.completed_blocks?.includes(block) && '✓'}
                              </span>
                            ))}
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteAssignment(assignment.assignment_id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Assign Blocks Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Assign Blocks to {selectedCaller?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Constituency</Label>
                <Select value={selectedConstituency} onValueChange={(v) => { setSelectedConstituency(v); setSelectedCampaign(''); setSelectedBlocks([]); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select constituency" />
                  </SelectTrigger>
                  <SelectContent>
                    {constituencies.map(c => (
                      <SelectItem key={c.constituency_id} value={c.constituency_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedConstituency && (
                <div>
                  <Label>Campaign</Label>
                  <Select value={selectedCampaign} onValueChange={(v) => { setSelectedCampaign(v); setSelectedBlocks([]); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select campaign" />
                    </SelectTrigger>
                    <SelectContent>
                      {campaigns.map(c => (
                        <SelectItem key={c.campaign_id} value={c.campaign_id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {selectedCampaign && blocks.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Select Blocks</Label>
                    <Button type="button" variant="link" size="sm" onClick={selectAllBlocks}>
                      Select All
                    </Button>
                  </div>
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-3 space-y-2">
                    {blocks.map(block => (
                      <div key={block} className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedBlocks.includes(block)}
                          onCheckedChange={() => toggleBlock(block)}
                        />
                        <label className="text-sm">Block {block}</label>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedBlocks.length} blocks selected
                  </p>
                </div>
              )}

              {selectedCampaign && blocks.length === 0 && (
                <p className="text-sm text-muted-foreground">No blocks found for this campaign</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
              <Button onClick={assignBlocks} disabled={selectedBlocks.length === 0}>
                Assign {selectedBlocks.length} Blocks
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
